import {
  ITableColumn,
  ITableDataItem,
  ITableFilter,
} from "@inceptionbg/ui-components";
import { deleteProps } from "@inceptionbg/ui-components";
import {
  IFieldEntryReportData,
  IFieldEntryReportFilters,
  EntryTypes,
} from "../../types/IFieldBook";
import { SelectAsyncPaginate } from "../../Components/Inputs/SelectAsyncPaginate";
import {
  getLandCultureOptions,
  getFieldsOptions,
} from "../../utils/LoadOptions";
import { Select } from "../../Components/Inputs/Select";
import { DateInput } from "@inceptionbg/ui-components";
import { formatCurrency } from "@inceptionbg/ui-components";

export const expensesByCategoryColumns = (
  t: (key: string) => string
): ITableColumn[] => [
  { id: "type", label: t("Type") },
  { id: "expense", label: t("Expenses"), align: "right" },
  { id: "income", label: t("Income"), align: "right" },
];

export const createExpensesByCategoryData = (
  reportData: IFieldEntryReportData | null,
  t: (key: string) => string
): ITableDataItem[] => {
  if (!reportData) return [];

  return (
    reportData.fieldEntryTypeReport?.map((item, index) => ({
      uuid: `expense-${index}`,
      cells: {
        type: {
          value: t(item.type),
        },
        expense: {
          value: formatCurrency(item.expense || 0),
        },
        income: {
          value: formatCurrency(item.income || 0),
        },
      },
    })) || []
  );
};

export const createExpensesByCategoryTotals = (
  reportData: IFieldEntryReportData | null,
  t: (key: string) => string
): ITableDataItem | undefined => {
  if (!reportData) return undefined;

  return {
    uuid: "expenses-totals",
    cells: {
      type: { value: t("Total") },
      expense: {
        value: formatCurrency(
          reportData.fieldEntryTypeReport?.reduce(
            (sum, item) => sum + (item.expense || 0),
            0
          ) || 0
        ),
      },
      income: {
        value: formatCurrency(
          reportData.fieldEntryTypeReport?.reduce(
            (sum, item) => sum + (item.income || 0),
            0
          ) || 0
        ),
      },
    },
  };
};

export const laborDataColumns = (
  t: (key: string) => string
): ITableColumn[] => [
  { id: "laborType", label: t("LaborType") },
  { id: "quantity", label: t("LaborHours"), align: "right" },
  { id: "expense", label: t("Expenses"), align: "right" },
];

export const createLaborDataData = (
  reportData: IFieldEntryReportData | null
): ITableDataItem[] => {
  if (!reportData) return [];

  return (
    reportData.laborTypeReport?.map((item, index) => ({
      uuid: `labor-${index}`,
      cells: {
        laborType: {
          value: item.laborType
            ? `${item.laborType.code} - ${item.laborType.name}`
            : "-",
        },
        quantity: {
          value: (item.quantity || 0).toFixed(2),
        },
        expense: {
          value: formatCurrency(item.expense || 0),
        },
      },
    })) || []
  );
};

export const productionResultsColumns = (
  t: (key: string) => string
): ITableColumn[] => [
  { id: "field", label: t("Field") },
  { id: "plantCulture", label: t("PlantCultureC") },
  { id: "expense", label: t("Expenses"), align: "right" },
  { id: "income", label: t("Income"), align: "right" },
  { id: "profit", label: t("NetProfit"), align: "right" },
];

export const createProductionResultsData = (
  reportData: IFieldEntryReportData | null
): ITableDataItem[] => {
  if (!reportData) return [];

  return (
    reportData.fieldEntryRecapitulations?.map((item, index) => ({
      uuid: `production-${index}`,
      cells: {
        field: {
          value: item.field?.name || "-",
        },
        plantCulture: {
          value: item.plantCulture
            ? `${item.plantCulture.code} - ${item.plantCulture.name}`
            : "-",
        },
        expense: {
          value: formatCurrency(item.expense || 0),
        },
        income: {
          value: formatCurrency(item.income || 0),
        },
        profit: {
          value: formatCurrency(item.profit || 0),
        },
      },
    })) || []
  );
};

export const createProductionResultsTotals = (
  reportData: IFieldEntryReportData | null,
  t: (key: string) => string
): ITableDataItem | undefined => {
  if (!reportData) return undefined;

  return {
    uuid: "production-totals",
    cells: {
      field: { value: t("Total") },
      plantCulture: { value: "" },
      expense: {
        value: formatCurrency(
          reportData.fieldEntryRecapitulations?.reduce(
            (sum, item) => sum + (item.expense || 0),
            0
          ) || 0
        ),
      },
      income: {
        value: formatCurrency(
          reportData.fieldEntryRecapitulations?.reduce(
            (sum, item) => sum + (item.income || 0),
            0
          ) || 0
        ),
      },
      profit: {
        value: formatCurrency(
          reportData.fieldEntryRecapitulations?.reduce(
            (sum, item) => sum + (item.profit || 0),
            0
          ) || 0
        ),
      },
    },
  };
};

export const reportFilters: (
  search: IFieldEntryReportFilters,
  setSearch: (search: IFieldEntryReportFilters) => void,
  t: (key: string) => string,
  farmUuid?: string
) => ITableFilter = (search, setSearch, t, farmUuid) => ({
  field: {
    label: t("Field"),
    field: (
      <SelectAsyncPaginate
        value={search.fieldObj || null}
        loadOptions={(searchText: string) =>
          getFieldsOptions(searchText, { farmUuid })
        }
        onChange={(e) => {
          setSearch(
            e
              ? {
                  ...search,
                  fieldUuid: e.value,
                  fieldObj: e,
                }
              : deleteProps(search, ["fieldUuid", "fieldObj"])
          );
        }}
        placeholder={t("ChoosePlaceholder")}
      />
    ),
    resetField: () => {
      setSearch(deleteProps(search, ["fieldUuid", "fieldObj"]));
    },
  },
  plantCulture: {
    label: t("PlantCultureC"),
    field: (
      <SelectAsyncPaginate
        value={search.plantCultureObj || null}
        loadOptions={(
          searchText: string,
          loadedOptions: [],
          offset: { page: number }
        ) =>
          getLandCultureOptions(searchText, loadedOptions, offset, {
            fieldUuid: search.fieldUuid,
          })
        }
        onChange={(e) => {
          setSearch(
            e
              ? {
                  ...search,
                  plantCultureUuid: e.value,
                  plantCultureObj: e,
                }
              : deleteProps(search, ["plantCultureUuid", "plantCultureObj"])
          );
        }}
        placeholder={t("ChoosePlaceholder")}
      />
    ),
    resetField: () => {
      setSearch(deleteProps(search, ["plantCultureUuid", "plantCultureObj"]));
    },
  },
  entryType: {
    label: t("Type"),
    field: (
      <Select
        value={search.entryTypeObj || null}
        options={EntryTypes.map((e) => ({ label: t(e), value: e }))}
        onChange={(e) => {
          setSearch(
            e
              ? {
                  ...search,
                  fieldEntryType: e.value,
                  entryTypeObj: e,
                }
              : deleteProps(search, ["fieldEntryType", "entryTypeObj"])
          );
        }}
      />
    ),
    resetField: () => {
      setSearch(deleteProps(search, ["fieldEntryType", "entryTypeObj"]));
    },
  },
  period: {
    label: `${t("Period")} ${t("from")}`,
    field: (
      <div className="flex-center full-width">
        <DateInput
          date={search.periodFrom || ""}
          setDate={(date) =>
            setSearch(
              date
                ? { ...search, periodFrom: date }
                : deleteProps(search, ["periodFrom"])
            )
          }
        />
        <p className="mx-2">{t("to")}</p>
        <DateInput
          date={search.periodTo || ""}
          setDate={(date) =>
            setSearch(
              date
                ? { ...search, periodTo: date }
                : deleteProps(search, ["periodTo"])
            )
          }
        />
      </div>
    ),
    resetField: () =>
      setSearch(deleteProps(search, ["periodFrom", "periodTo"])),
  },
});

